/**
 * @file
 * Commentary sorting algorithms.
 * See https://refstudycentre.atlassian.net/wiki/spaces/VV/pages/2062942310/Grouping+and+sorting
 */
import type { VidRange } from "~/units/scripture";
import { rangesOverlap } from "~/units/scripture";
import type {
  InnerCommentaryGroup,
  OuterCommentaryGroup,
  ProcessedCommentaryBase,
} from "./types";
import { getTidLocale } from "~/static/translations";

const bComesFirst = 1;
const aComesFirst = -1;
const noPreference = 0;

/**
 * Decide which heading comes first.
 *
 * @returns
 *   1 if group b comes first, -1 if group a comes first, 0 if undecided.
 */
function compareHeadings(headingA: string, headingB: string): number {
  const aHasExplicitGroupHeading = headingA.length > 0;
  const bHasExplicitGroupHeading = headingB.length > 0;

  // The "General" heading always comes last.
  if (headingA.toLowerCase() === "general") {
    return bComesFirst;
  }
  if (headingB.toLowerCase() === "general") {
    return aComesFirst;
  }

  if (aHasExplicitGroupHeading && bHasExplicitGroupHeading) {
    // Explicit headings are sorted alphabetically.
    return headingA.localeCompare(headingB);
  }

  // Explicit headings come before generated headings.
  if (aHasExplicitGroupHeading) {
    return aComesFirst;
  }
  if (bHasExplicitGroupHeading) {
    return bComesFirst;
  }

  return noPreference;
}

/**
 * Decide which verse range comes first.
 *
 * 1. Ranges come before nulls.
 * 2. Sort by canonical order of the starting verse.
 * 3. Broad vs. narrow:
 *   - Smaller range first if “narrow” sorting is used.
 *   - Larger range first if “broad” sorting is used.
 * 4. Sort by translation. Translation-agnostic before translation-specific.
 *
 * @param rangeA
 *   The first range.
 * @param rangeB
 *   The second range.
 * @param narrow
 *   true: Narrow ranges come first.
 *   false: Broad ranges come first.
 *
 * @returns
 *   1 if group b comes first, -1 if group a comes first, 0 if undecided.
 */
function compareRanges(
  rangeA: VidRange | null | undefined,
  rangeB: VidRange | null | undefined,
  narrow: boolean,
): number {
  if (!rangeA && !rangeB) {
    // Nothing to compare.
    return noPreference;
  }

  // Ranges come before nulls.
  if (!rangeB) {
    return aComesFirst;
  }
  if (!rangeA) {
    return bComesFirst;
  }

  // Sort by canonical order of the starting verse.
  if (rangeA.fromVid !== rangeB.fromVid) {
    return rangeA.fromVid > rangeB.fromVid ? bComesFirst : aComesFirst;
  }

  // Broad vs. narrow verse range.
  // Calculate the number of VIDs that the comments cover.
  // Here we assume that the VIDs are equidistant, which is true most of the time.
  const aRangeSpan = rangeA.toVid - rangeA.fromVid;
  const bRangeSpan = rangeB.toVid - rangeB.fromVid;
  if (aRangeSpan !== bRangeSpan) {
    if (narrow) {
      // Groups that span smaller verse ranges come first.
      return aRangeSpan > bRangeSpan ? bComesFirst : aComesFirst;
    }

    // Groups that span larger verse ranges come first.
    return aRangeSpan < bRangeSpan ? bComesFirst : aComesFirst;
  }

  // Sort by translation. Translation-agnostic before translation-specific.
  if (rangeA.tid && rangeB.tid) {
    // Both are translation-specific.
    if (rangeA.tid !== rangeB.tid) {
      // Sort alphabetically by ID (maybe not the best, but at least it keeps the similar ones together).
      return rangeA.tid.localeCompare(rangeB.tid);
    }
    // Translations are the same.
  } else if (rangeA.tid) {
    // A is translation-specific.
    return bComesFirst;
  } else if (rangeB.tid) {
    // B is translation-specific.
    return aComesFirst;
  } // else both are translation agnostic.

  // Sort by word ordinals.
  const wordOrdinalSort = compareWordOrdinals(
    rangeA.wordOrdinals,
    rangeB.wordOrdinals,
    narrow,
  );
  if (wordOrdinalSort !== noPreference) {
    return wordOrdinalSort;
  }

  // The ranges are exactly the same.
  return noPreference;
}

/**
 * For two otherwise identical ranges, decide which one comes first based on the word ordinals.
 *
 * @param a
 *   The word ordinals of the first range.
 * @param b
 *   The word ordinals of the second range.
 * @param narrow
 *   true: Fewer word ordinals come first.
 *   false: More word ordinals come first.
 *
 * @returns
 *   1 if group b comes first, -1 if group a comes first, 0 if undecided.
 */
function compareWordOrdinals(
  a: number[],
  b: number[],
  narrow: boolean,
): number {
  if (!a.length && !b.length) {
    // Nothing to compare.
    return noPreference;
  }

  // Ranges without word ordinals come first.
  if (!a.length || a[0] === undefined) {
    return aComesFirst;
  }
  if (!b.length || b[0] === undefined) {
    return bComesFirst;
  }

  // Sort by the earliest word ordinal.
  if (a[0] !== b[0]) {
    return a[0] > b[0] ? bComesFirst : aComesFirst;
  }

  // Broad vs. narrow word ordinals.
  // A range having more word ordinals is assumed to mean "broad".
  if (a.length !== b.length) {
    if (narrow) {
      // Ranges with fewer ordinals come first.
      return a.length > b.length ? bComesFirst : aComesFirst;
    }

    // Ranges with more ordinals come first.
    return a.length < b.length ? bComesFirst : aComesFirst;
  }

  return noPreference;
}

/**
 * Decide which outer commentary group comes first.
 *
 * @param a
 *   The first group.
 * @param b
 *   The second group.
 * @param narrow
 *   true: Groups with narrow ranges come first.
 *   false: Groups with broad ranges come first.
 *
 * @returns
 *   1 if group b comes first, -1 if group a comes first, 0 if undecided.
 */
export function compareOuterGroups(
  a: OuterCommentaryGroup,
  b: OuterCommentaryGroup,
  narrow: boolean,
): number {
  let x;

  x = compareHeadings(a.heading || "", b.heading || "");
  if (x !== noPreference) {
    return x;
  }

  // There are no headings.

  x = compareRanges(a.range, b.range, narrow);
  if (x !== noPreference) {
    return x;
  }

  // The ranges are exactly the same or both `null`.

  return noPreference;
}

/**
 * Decide which inner commentary group comes first.
 *
 * 1. Sort by canonical order of the starting verse.
 * 2. Broad vs. narrow:
 *   - If `broad`, range groups come before single-verse groups.
 *   - If `narrow`, single-verse groups come before range groups.
 * 3. Single-verse groups:
 *   3.1. Sort by translation.
 *   3.2. Sort by verse-part label (whole verse first, then parts a, b, c, ...)
 * 4. Range groups: Use `compareRanges`.
 *
 * @param a
 *   The first group.
 * @param b
 *   The second group.
 * @param narrow
 *   true: Groups with narrow ranges come first.
 *   false: Groups with broad ranges come first.
 * @param languages
 *   An array of languages in order of priority.
 *   The app language should be first in this array, followed by other languages that the user can read.
 *
 * @returns
 *   1 if group b comes first, -1 if group a comes first, 0 if undecided.
 */
export function compareInnerGroups(
  a: InnerCommentaryGroup,
  b: InnerCommentaryGroup,
  narrow: boolean,
  languages: string[],
): number {
  let x;

  // Sort by most preferred languages.
  const aLanguageOrdinal = languages.indexOf(getTidLocale(a.verse?.tid));
  const bLanguageOrdinal = languages.indexOf(getTidLocale(b.verse?.tid));
  if (
    aLanguageOrdinal !== -1 &&
    bLanguageOrdinal !== -1 &&
    aLanguageOrdinal !== bLanguageOrdinal
  ) {
    return aLanguageOrdinal < bLanguageOrdinal ? aComesFirst : bComesFirst;
  }

  // Sort by canonical order of the starting verse.
  const fromVidA = a.verse?.fromVid || a.range?.fromVid || 0;
  const fromVidB = b.verse?.fromVid || b.range?.fromVid || 0;
  if (fromVidA !== fromVidB) {
    return fromVidA > fromVidB ? bComesFirst : aComesFirst;
  }

  // Broad vs narrow.
  if (a.range && b.verse) {
    if (narrow) {
      return bComesFirst;
    }
    return aComesFirst;
  }
  if (a.verse && b.range) {
    if (narrow) {
      return aComesFirst;
    }
    return bComesFirst;
  }

  if (a.verse && b.verse) {
    // Single-verse groups.

    // Sort by translation.
    if (a.verse.tid !== b.verse.tid) {
      return a.verse.tid.localeCompare(b.verse.tid);
    }

    if (a.verse.tr !== b.verse.tr) return a.verse.tr.localeCompare(b.verse.tr);

    // Sort by verse-part label (whole verse first, then parts a, b, c, ...)
    const versePartA = a.versePart || "";
    const versePartB = b.versePart || "";
    if (versePartA !== versePartB) {
      return versePartA.localeCompare(versePartB);
    }
  }

  if (a.range && b.range) {
    // Range groups: Use `compareRanges`.
    x = compareRanges(a.range, b.range, narrow);
    if (x !== noPreference) {
      return x;
    }
  }

  return noPreference;
}

/**
 * Decide which comment comes first based on the language of the comments and the user's language preferences.
 *
 * @param a The first comment.
 * @param b The second comment.
 * @param languages
 *   An array of languages in order of priority.
 *   The app language should be first in this array, followed by other languages that the user can read.
 */
export function compareCommentLanguages(
  a: ProcessedCommentaryBase,
  b: ProcessedCommentaryBase,
  languages: string[],
): number {
  const aOrdinal = languages.indexOf(a.language);
  const bOrdinal = languages.indexOf(b.language);

  if (aOrdinal === bOrdinal) {
    return noPreference;
  }

  return aOrdinal < bOrdinal ? aComesFirst : bComesFirst;
}

/**
 * Decide which comment comes first.
 *
 * 1. Sort by language (app language first)
 * 2. Sort by scripture ranges.
 * 3. Sort by weight.
 *
 * @param a
 *   The first comment.
 * @param b
 *   The second comment.
 * @param narrow
 *   true: Comments with narrow ranges come first.
 *   false: Comments with broad ranges come first.
 * @param groupRange
 *   The range of the group that contains these comments.
 * @param languages
 *   An array of languages in order of priority.
 *   The app language should be first in this array, followed by other languages that the user can read.
 *
 * @returns
 *   1 if comment b comes first, -1 if comment a comes first, 0 if undecided.
 */
export function compareComments(
  a: ProcessedCommentaryBase,
  b: ProcessedCommentaryBase,
  narrow: boolean,
  groupRange: VidRange | undefined,
  languages: string[],
): number {
  // Sort by language.
  if (a.language !== b.language) {
    return compareCommentLanguages(a, b, languages);
  }

  // Sort by scripture ranges.
  // If there is more than one range, use the first one that overlaps with the range of the group containing these
  // comments. If there is no groupRange, use the first range of each.
  const aRange = groupRange
    ? a.ranges.find((r) => rangesOverlap(r, groupRange))
    : a.ranges[0];
  const bRange = groupRange
    ? b.ranges.find((r) => rangesOverlap(r, groupRange))
    : b.ranges[0];
  const rangeOrder = compareRanges(aRange, bRange, narrow);
  if (rangeOrder !== noPreference) {
    return rangeOrder;
  }

  // Sort by weight. Heavier items sink to the bottom.
  if (a.weight !== b.weight) {
    return a.weight > b.weight ? bComesFirst : aComesFirst;
  }

  return noPreference;
}
